import React, { useCallback, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, GeoJSON, Marker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getGeoJsonLatLngBounds } from './zipcodeMapCache';
import {
  TILE_URLS,
  DEFAULT_MAP_CENTER,
  TRACKING_STATUS,
  ZIP_BOUNDARY_STYLE,
  MAP_MARKER_VIEW,
} from './constants';
import { MapControls } from './MapControls';
import { OrderTimeline } from './OrderTimeline';
import { useCachedZipcodeGeoJson } from './useCachedZipcodeGeoJson';

function FitMapBounds({ positions, focusPositions, geoJson }) {
  const map = useMap();

  useEffect(() => {
    const focusingOrder = Boolean(focusPositions?.length);
    const target = focusingOrder ? focusPositions : positions;

    let bounds = null;

    if (target?.length === 1) {
      bounds = L.latLngBounds([target[0]]);
    } else if (target?.length > 1) {
      bounds = L.latLngBounds(target);
    }

    if (!focusingOrder && geoJson) {
      const geoBounds = getGeoJsonLatLngBounds(geoJson);
      if (geoBounds) {
        bounds = bounds ? bounds.extend(geoBounds) : geoBounds;
      }
    }

    if (!bounds?.isValid()) return;

    if (focusingOrder && target.length === 1) {
      map.setView(target[0], 14);
      return;
    }

    map.fitBounds(bounds, {
      padding: [80, 80],
      maxZoom: focusingOrder ? 15 : 13,
    });
  }, [positions, focusPositions, geoJson, map]);

  return null;
}

function MapZoomBridge({ onReady }) {
  const map = useMap();
  useEffect(() => {
    onReady(map);
  }, [map, onReady]);
  return null;
}

const RESTAURANT_OVERVIEW_ORANGE = '#EA580C';
const CUSTOMER_OVERVIEW_BLACK = '#18181b';

function createMarkerIcon(type, { active = false, overview = false } = {}) {
  const compact = type === 'restaurant' || type === 'customer' ? true : overview;
  const size = compact ? 20 : 28;
  const anchor = size / 2;

  const styles = {
    restaurant: {
      bg: RESTAURANT_OVERVIEW_ORANGE,
    },
    rider: { bg: active ? '#0f0f10' : '#18181b' },
    customer: {
      bg: CUSTOMER_OVERVIEW_BLACK,
    },
  };
  const s = styles[type] ?? styles.customer;
  const pulse = active ? 'otd-marker-pulse' : '';
  const emoji = type === 'restaurant' ? '🍽' : type === 'rider' ? '🛵' : '📍';
  const markerClass = ['otd-marker', compact ? 'otd-marker--compact' : '', pulse]
    .filter(Boolean)
    .join(' ');

  return L.divIcon({
    className: '',
    html: `<div class="${markerClass}" style="background:${s.bg}"><span>${emoji}</span></div>`,
    iconSize: [size, size],
    iconAnchor: [anchor, anchor],
  });
}

function OrderRoute({ order, selected }) {
  const { restaurantPosition, customerPosition, riderPosition, status } = order;
  if (!restaurantPosition || !customerPosition) return null;

  const active = selected;
  const rider = riderPosition ?? customerPosition;

  const completedPath =
    status === TRACKING_STATUS.PREPARING
      ? [restaurantPosition]
      : [restaurantPosition, rider];

  const remainingPath =
    status === TRACKING_STATUS.DELIVERED
      ? []
      : status === TRACKING_STATUS.PREPARING
        ? [restaurantPosition, customerPosition]
        : [rider, customerPosition];

  return (
    <>
      {completedPath.length >= 2 && (
        <Polyline
          positions={completedPath}
          pathOptions={{
            color: '#111827',
            weight: active ? 5 : 4,
            opacity: 0.95,
            className: active ? 'otd-route-line otd-route-line--animated' : 'otd-route-line',
          }}
        />
      )}
      {remainingPath.length >= 2 && (
        <Polyline
          positions={remainingPath}
          pathOptions={{
            color: '#111827',
            weight: 4,
            opacity: 0.75,
            dashArray: '10 10',
            className: 'otd-route-line otd-route-line--dashed',
          }}
        />
      )}
    </>
  );
}

function MapMarkerLegend() {
  return (
    <div className='otd-map-legend' aria-label='Map marker legend'>
      <span className='otd-map-legend__item'>
        <span className='otd-map-legend__dot otd-map-legend__dot--restaurant' aria-hidden />
        Restaurant
      </span>
      <span className='otd-map-legend__item'>
        <span className='otd-map-legend__dot otd-map-legend__dot--customer' aria-hidden />
        Customer
      </span>
    </div>
  );
}

export function TrackingMap({
  orders,
  selectedOrder,
  selectedId,
  mapMarkerView,
  mapStyle,
  onMapStyleChange,
  onCloseTimeline,
  onSelectOrder,
  mapRef,
  setMapRef,
}) {
  const selected = selectedOrder;
  const zipBoundaryGeoJson = useCachedZipcodeGeoJson();
  const isRestaurantView = mapMarkerView === MAP_MARKER_VIEW.RESTAURANT;
  const isCustomerView = mapMarkerView === MAP_MARKER_VIEW.CUSTOMER;
  const isBothView = mapMarkerView === MAP_MARKER_VIEW.BOTH;
  const showRestaurants = isRestaurantView || isBothView;
  const showCustomers = isCustomerView || isBothView;

  const getZipBoundaryStyle = useCallback(() => ZIP_BOUNDARY_STYLE, []);

  const focusPositions = useMemo(() => {
    if (!selected) return null;
    if (isRestaurantView || isBothView) {
      const pts = [];
      if (selected.restaurantPosition) pts.push(selected.restaurantPosition);
      if (selected.customerPosition) pts.push(selected.customerPosition);
      if (selected.riderPosition) pts.push(selected.riderPosition);
      return pts.length ? pts : null;
    }
    if (isCustomerView && selected.customerPosition) {
      return [selected.customerPosition];
    }
    return null;
  }, [selected, isRestaurantView, isBothView, isCustomerView]);

  const restaurantOverviewPositions = useMemo(
    () => orders.map((o) => o.restaurantPosition).filter(Boolean),
    [orders]
  );

  const customerOverviewPositions = useMemo(
    () => orders.map((o) => o.customerPosition).filter(Boolean),
    [orders]
  );

  const showOrderRoute = Boolean(
    selectedId && selected && (isRestaurantView || isBothView)
  );
  /** In "Both" mode keep all pins visible even when an order is selected. */
  const showOverviewMarkers = isBothView || !showOrderRoute;

  const mapFitPositions = useMemo(() => {
    if (showOrderRoute && selected) {
      const pts = [];
      if (selected.restaurantPosition) pts.push(selected.restaurantPosition);
      if (selected.customerPosition) pts.push(selected.customerPosition);
      if (selected.riderPosition) pts.push(selected.riderPosition);
      return pts;
    }
    if (isBothView) {
      return [...restaurantOverviewPositions, ...customerOverviewPositions];
    }
    if (isCustomerView) return customerOverviewPositions;
    return restaurantOverviewPositions;
  }, [
    showOrderRoute,
    selected,
    isBothView,
    isCustomerView,
    customerOverviewPositions,
    restaurantOverviewPositions,
  ]);

  const center = useMemo(() => {
    if (selected?.customerPosition && (showOrderRoute || isCustomerView)) {
      return selected.customerPosition;
    }
    if (isBothView && (restaurantOverviewPositions[0] || customerOverviewPositions[0])) {
      return restaurantOverviewPositions[0] ?? customerOverviewPositions[0];
    }
    if (isCustomerView && customerOverviewPositions[0]) return customerOverviewPositions[0];
    if (restaurantOverviewPositions[0]) return restaurantOverviewPositions[0];
    const bounds = zipBoundaryGeoJson ? getGeoJsonLatLngBounds(zipBoundaryGeoJson) : null;
    if (bounds?.isValid()) return bounds.getCenter();
    return DEFAULT_MAP_CENTER;
  }, [
    selected,
    showOrderRoute,
    isBothView,
    isCustomerView,
    customerOverviewPositions,
    restaurantOverviewPositions,
    zipBoundaryGeoJson,
  ]);

  const handleLocate = () => {
    if (!mapRef || !selected?.customerPosition) return;
    mapRef.setView(selected.customerPosition, 15);
  };

  return (
    <div className='otd-map-wrap'>
      <div className='otd-map-body'>
        <MapContainer
          center={center}
          zoom={12}
          scrollWheelZoom
          className='otd-map'
          zoomControl={false}
        >
          <TileLayer url={TILE_URLS[mapStyle] ?? TILE_URLS.roadmap} attribution='&copy; OpenStreetMap' />
          <MapZoomBridge onReady={setMapRef} />

          {zipBoundaryGeoJson ? (
            <GeoJSON
              key={zipBoundaryGeoJson.features.length}
              data={zipBoundaryGeoJson}
              style={getZipBoundaryStyle}
              interactive={false}
            />
          ) : null}

          {showOrderRoute &&
            orders
              .filter((order) => order.id === selectedId)
              .map((order) => (
                <OrderRoute key={`route-${order.id}`} order={order} selected />
              ))}

          {showOrderRoute &&
            !isBothView &&
            orders
              .filter((order) => order.id === selectedId)
              .map((order) => (
                <React.Fragment key={`detail-${order.id}`}>
                  {order.restaurantPosition && (
                    <Marker
                      position={order.restaurantPosition}
                      icon={createMarkerIcon('restaurant', { active: true })}
                      zIndexOffset={500}
                    />
                  )}
                  {order.customerPosition && (
                    <Marker
                      position={order.customerPosition}
                      icon={createMarkerIcon('customer', { active: true })}
                      zIndexOffset={480}
                    />
                  )}
                  {order.riderPosition && (
                    <Marker
                      position={order.riderPosition}
                      icon={createMarkerIcon('rider', { active: true })}
                      zIndexOffset={600}
                    />
                  )}
                </React.Fragment>
              ))}

          {showOverviewMarkers &&
            showRestaurants &&
            orders.map((order) => {
              if (!order.restaurantPosition) return null;
              const isSelected = order.id === selectedId;
              return (
                <Marker
                  key={`r-${order.id}`}
                  position={order.restaurantPosition}
                  icon={createMarkerIcon('restaurant', {
                    overview: true,
                    active: isSelected,
                  })}
                  zIndexOffset={isSelected ? 520 : 100}
                  eventHandlers={
                    onSelectOrder ? { click: () => onSelectOrder(order.id) } : undefined
                  }
                />
              );
            })}

          {showOverviewMarkers &&
            showCustomers &&
            orders.map((order) => {
              if (!order.customerPosition) return null;
              const isSelected = order.id === selectedId;
              return (
                <Marker
                  key={`c-${order.id}`}
                  position={order.customerPosition}
                  icon={createMarkerIcon('customer', {
                    overview: true,
                    active: isSelected,
                  })}
                  zIndexOffset={isSelected ? 500 : 80}
                  eventHandlers={
                    onSelectOrder ? { click: () => onSelectOrder(order.id) } : undefined
                  }
                />
              );
            })}

          {showOrderRoute &&
            isBothView &&
            orders
              .filter((order) => order.id === selectedId)
              .map((order) => (
                <React.Fragment key={`both-detail-${order.id}`}>
                  {order.riderPosition && (
                    <Marker
                      position={order.riderPosition}
                      icon={createMarkerIcon('rider', { active: true })}
                      zIndexOffset={600}
                    />
                  )}
                </React.Fragment>
              ))}

          <FitMapBounds
            positions={mapFitPositions}
            focusPositions={focusPositions}
            geoJson={zipBoundaryGeoJson}
          />
        </MapContainer>

        <MapControls
          mapStyle={mapStyle}
          onMapStyleChange={onMapStyleChange}
          onLocate={handleLocate}
          onZoomIn={() => mapRef?.zoomIn()}
          onZoomOut={() => mapRef?.zoomOut()}
        />

        <MapMarkerLegend />

        {selectedId && selected && (
          <OrderTimeline order={selected} onClose={onCloseTimeline} />
        )}
      </div>
    </div>
  );
}
